import { UIKit } from "@/shared/ui";
/**
 * Свойства потолка выделенной комнаты: вид из каталога, опуск от перекрытия и цвет.
 *
 * Показывается вместо свойств пола, когда включён слой потолков — на плане в этот
 * момент видно именно потолок. Чистовая высота комнаты считается из высоты стен
 * за вычетом опуска: это то, что увидит пользователь, и от неё висят светильники.
 */
import { CATALOG, ceilingLook, getCeilingMaterial } from "@/shared/catalog";
import { useProjectStore } from "@/shared/store";
import { useCeilingStore } from "@/shared/store/layers/ceilingStore";
import { formatArea } from "@/shared/lib/geometry";
import { roomCeilingHeight, type Room } from "@/widgets/plan";

interface Props {
	room: Room;
}

export const RoomCeilingProperties = ({ room }: Props) => {
	const graph = useProjectStore((s) => s.project.layers.walls);
	const ceiling = useProjectStore((s) => s.project.layers.ceiling.items[room.id]);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const setRoomCeilingMaterial = useCeilingStore((s) => s.setRoomCeilingMaterial);
	const updateRoomCeiling = useCeilingStore((s) => s.updateRoomCeiling);
	const removeRoomCeiling = useCeilingStore((s) => s.removeRoomCeiling);

	const slab = roomCeilingHeight(graph, room);
	const look = ceiling ? ceilingLook(ceiling) : null;
	const minDrop = look ? getCeilingMaterial(look.material).minDrop : 0;

	return (
		<>
			<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
				Потолок комнаты
			</UIKit.Text>
			<div className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
				Площадь {formatArea(room.area)} • до перекрытия {slab} мм
				{look && ` • чистовая высота ${slab - look.drop} мм`}
			</div>
			{!ceiling && (
				<UIKit.Text className="text-xs leading-copy text-amber-800">
					Потолок не назначен — выберите вид, и комната получит его с типовым опуском.
				</UIKit.Text>
			)}
			<div className="flex flex-col gap-1.5">
				{CATALOG.ceiling.items.map((item) => {
					const active = ceiling?.material === item.material;
					return (
						<button
							key={item.id}
							type="button"
							title={item.description}
							className={`flex items-center justify-between rounded-lg border px-2.5 py-2 text-left text-sm transition-colors ${
								active
									? "border-accent bg-accent-soft text-accent"
									: "border-border bg-surface text-ink hover:border-slate-300"
							}`}
							onClick={() => {
								if (active) return;
								pushHistory();
								setRoomCeilingMaterial(room.id, item.material);
							}}
						>
							<UIKit.Text as="span" className="flex items-center gap-2">
								<UIKit.Text
									as="span"
									className="size-2.5 rounded-sm border border-border"
									style={{ background: item.color }}
								/>
								{item.name}
							</UIKit.Text>
							{active ? "выбран" : `−${item.drop} мм`}
						</button>
					);
				})}
			</div>
			{look && (
				<div className="mt-1 flex flex-col gap-2">
					<UIKit.NumberField
						label="Опуск от перекрытия"
						value={look.drop}
						min={minDrop}
						max={Math.max(minDrop, slab - 2000)}
						step={10}
						onChange={(drop) => {
							pushHistory();
							updateRoomCeiling(room.id, { drop });
						}}
					/>
					<UIKit.ColorPicker
						label="Цвет"
						value={look.color}
						onChange={(color) => {
							pushHistory();
							updateRoomCeiling(room.id, { color });
						}}
					/>
					<button
						type="button"
						className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
						onClick={() => {
							pushHistory();
							removeRoomCeiling(room.id);
						}}
					>
						Убрать потолок
					</button>
				</div>
			)}
		</>
	);
};
